import React, { FC, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Table, TableColumn, TableSort, Pager, Text } from "@foris/avocado-suite";
import LinkRouter from "@common/components/link-router";
import { Icon, IconTypes } from "@foris/avocado-icons";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import * as mouseflow from "@utils/mouseflow";
import { getColumnCell, getPillCell } from "@common/utils/table.util";
import { ContextEDH } from "@context/ContextEDH";
import {
  GroupChange,
  GroupsManagerGroupChangesOrderByFieldsEnum,
  PageInfo,
  OrderByDirection,
} from "@models/ISchema";
import { TableFiltersReducerType } from "../../context/types";
import { Types as TableFiltersTypes } from "@modules/sections/GroupsManager/context/tableFilters.reducer";
import { Context } from "../../context/GroupsManagerContext";
import scss from "./groupsHistoryTable.module.scss";

/**
 * Props interface for the GroupsHistoryTable component
 * Handles displaying and interacting with group change history data
 */
interface GroupsHistoryTableProps {
  contextUrl?: string;
  data: GroupChange[];
  requestGroupChanges?: (page: number, orderBy?: any, searchBy?: string, size?: number) => void;
  pageInfo: PageInfo;
  pageSize?: number;
  requestGroupCapacityChangeDetails?: (
    page: number,
    orderBy: TableFiltersReducerType["orderBy"],
    searchBy: TableFiltersReducerType["searchBy"],
  ) => void;
  onChangeSizePerPage?: (value: number) => void;
  setColumns?: (columns: any[]) => void;
}

const GroupsHistoryTable: FC<GroupsHistoryTableProps> = ({
  data,
  pageInfo,
  pageSize,
  onChangeSizePerPage,
  requestGroupCapacityChangeDetails,
  setColumns,
}) => {
  const { state, dispatch } = useContext(Context);
  const { state: outerState } = useContext(ContextEDH);

  const { t } = useTranslation();
  const { groupDetailUrl } = useNavigationUrl();

  // Configure pagination settings with memoization to prevent unnecessary recalculations
  const pagination = useMemo(() => {
    return {
      onChange: (page: number) => {
        requestGroupCapacityChangeDetails?.(
          page,
          state?.tableFilters?.orderBy,
          state?.tableFilters?.searchBy,
        );
      },
      total: pageInfo?.total ?? 0,
      page: pageInfo?.page ?? 1,
      size: pageSize,
      classname: { global: scss.groupsHistoryTable_pagerContainer },
      showRowsPerPage: !!onChangeSizePerPage,
      pageSizeOptions: [5, 20],
      labelRowsPerPage: t("group-changes-history.pager.label-rows-per-page"),
      onRowsPerPageChange: (size: { value: number }) => onChangeSizePerPage?.(size.value),
    };
  }, [pageInfo, state?.tableFilters?.orderBy, state?.tableFilters?.searchBy, t]);

  /**
   * Handles column header clicks for sorting
   * Updates the global state with new sort parameters and triggers data refetch
   */
  const onHeaderClick = (column: TableSort) => {
    const newOrderBy = {
      direction: column.direction.toUpperCase() as OrderByDirection,
      field: column.column as any,
      header: "" as any,
    };

    dispatch({
      type: TableFiltersTypes.SetOrderBy,
      payload: newOrderBy,
    });
    requestGroupCapacityChangeDetails?.(1, newOrderBy, state?.tableFilters?.searchBy);
  };

  // Define table columns with their rendering logic and sort capabilities
  // Each column uses type assertions to safely access GroupChange properties
  const columns: TableColumn[] = [
    {
      key: GroupsManagerGroupChangesOrderByFieldsEnum.Date,
      header: t("group-changes-history.table.date"),
      renderCell: (data: unknown) => getColumnCell((data as GroupChange)?.createdAt),
      isSortable: true,
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "180px" },
      },
    },

    {
      key: GroupsManagerGroupChangesOrderByFieldsEnum.Status,
      header: t("group-changes-history.table.state"),
      renderCell: (data: unknown) => {
        // Type assertion pattern used consistently across all renderCell functions
        // for type safety while maintaining compatibility with the Table component
        const groupChange = data as GroupChange;
        const item = {
          SUCCESS: { translation: "Guardado" },
          FAILED: { translation: "Fallado" },
          PENDING: { translation: "Pendiente" },
        }[groupChange?.status];
        return getPillCell(item?.translation);
      },
      isSortable: true,
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "140px" },
      },
    },
    {
      key: GroupsManagerGroupChangesOrderByFieldsEnum.GroupCode,
      header: t("group-changes-history.table.crn"),
      renderCell: (data: unknown) => {
        // Type assertion pattern used consistently across all renderCell functions
        // for type safety while maintaining compatibility with the Table component
        const groupChange = data as GroupChange;
        return (
          <LinkRouter
            colored
            to={groupDetailUrl(groupChange?.group?.id)}
            onClick={() => {
              mouseflow.actionTag(
                "action_groups_manager_enrollments",
                outerState?.base?.isMouseflowEnabled,
              );
            }}
          >
            {groupChange?.group?.code ?? "-"}
          </LinkRouter>
        );
      },
      isSortable: true,
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "140px" },
      },
    },

    {
      key: GroupsManagerGroupChangesOrderByFieldsEnum.Campus,
      header: t("group-changes-history.table.campus"),
      renderCell: (data: unknown) =>
        getColumnCell((data as GroupChange)?.group?.campus?.code ?? "--"),
      isSortable: true,
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "140px" },
      },
    },
    {
      key: GroupsManagerGroupChangesOrderByFieldsEnum.CourseName,
      header: t("group-changes-history.table.course"),
      renderCell: (groupChange: any) => getColumnCell(groupChange?.group?.course?.name ?? "--"),
      isSortable: true,
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "140px" },
      },
    },
    {
      key: GroupsManagerGroupChangesOrderByFieldsEnum.ChangeType,
      header: t("group-changes-history.table.type-change"),
      renderCell: (groupChange: any) => {
        const prop = groupChange?.propertyUpdated === "capacity" ? "Cupos" : "Visibilidad";
        return getColumnCell(prop);
      },
      isSortable: true,
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "140px" },
      },
    },
    {
      key: GroupsManagerGroupChangesOrderByFieldsEnum.Detail,
      header: t("group-changes-history.table.detail"),
      renderCell: (groupChange: any) => {
        /**
         * Determines which icon to display based on capacity change direction
         * - Up arrow for increases
         * - Down arrow for decreases
         * - Minus for no change
         */
        const getCapacityIcon = (before: string, after: string): IconTypes => {
          if (before < after) return "chevron-up";
          if (before === after) return "minus";
          else return "chevron-down";
        };

        const current = groupChange?.current;
        const expected = groupChange?.expected;

        if (groupChange?.propertyUpdated === "capacity") {
          return (
            <Text>
              {current} | {expected}
              <Icon name={getCapacityIcon(current, expected)} />
            </Text>
          );
        }

        return (
          <span>
            <Icon name={current === "0" ? "closed-eye" : "open-eye"} />
            <Icon name={expected === "0" ? "closed-eye" : "open-eye"} />
          </span>
        );
      },
      isSortable: true,
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "140px" },
      },
    },
    {
      key: GroupsManagerGroupChangesOrderByFieldsEnum.Responsible,
      header: t("group-changes-history.table.responsible"),
      renderCell: (groupChange: any) => {
        return getColumnCell(groupChange?.username ?? "-");
      },
      isSortable: true,
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "140px" },
      },
    },
  ];

  useEffect(() => {
    setColumns(columns);
  }, []);

  // Render the table with sorting capabilities and pagination
  return (
    <section className={scss.groupsHistoryTable}>
      <Table
        columns={columns as TableColumn[]}
        data={data ?? []}
        desktopVariant="regular"
        onSortChange={onHeaderClick}
        // Set initial sort state from the context to maintain sort preferences
        initialSort={
          {
            column: state?.tableFilters?.orderBy?.field,
            direction: state?.tableFilters?.orderBy?.direction?.toLowerCase() ?? "desc",
          } as TableSort
        }
      />

      {!!pageInfo?.total && <Pager {...pagination} />}
    </section>
  );
};

export default GroupsHistoryTable;
